import { isTravelNavigationActive } from '@/lib/travel-navigation';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Users,
  Package,
  FileText,
  CalendarCheck,
  Receipt,
  Settings,
  Settings2,
  ChevronLeft,
  ChevronRight,
  Calculator,
  Compass,
  Wallet,
  Layers,
  Ticket,
  MessageSquare,
  Tags,
  Boxes,
  ClipboardList,
  Store,
  Bell,
  Calendar,
  Warehouse,
  ArrowLeftRight,
  PackageSearch,
  TrendingUp,
  TrendingDown,
  Truck,
  FileCheck,
  CreditCard,
  FileMinus,
  FileInput,
  Handshake,
  Scale,
  History,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppStore, type PermissionModule } from '../../stores/app-store';
import { AppSelectorCompact } from './app-selector';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  isMobile?: boolean;
}

// Module to permission mapping for Travel
const TRAVEL_MODULE_MAP: Record<string, PermissionModule> = {
  dashboard: 'DASHBOARD',
  outgoingBookings: 'BOOKINGS',
  incomingBookings: 'BOOKINGS',
  outgoingInvoices: 'INVOICES',
  incomingInvoices: 'INVOICES',
  travelCariStatement: 'ACCOUNTING',
  travelLedger: 'ACCOUNTING',
  travelCashBook: 'ACCOUNTING',
  travelTrialBalance: 'ACCOUNTING',
  landTours: 'PACKAGES',
  cruise: 'PACKAGES',
  travelCariKartlar: 'CUSTOMERS',
  travelCariTypes: 'ACCOUNTING',
  customers: 'CUSTOMERS',
  partners: 'PARTNERS',
  packages: 'PACKAGES',
  quotes: 'QUOTES',
  bookings: 'BOOKINGS',
  invoices: 'INVOICES',
  expenses: 'EXPENSES',
  allocations: 'ALLOCATIONS',
  operations: 'OPERATIONS',
  notifications: 'NOTIFICATIONS',
  promoCodes: 'PROMO_CODES',
  reviews: 'REVIEWS',
  accounting: 'ACCOUNTING',
  postingRules: 'ACCOUNTING',
  tieOut: 'ACCOUNTING',
  periodEnd: 'ACCOUNTING',
  travelCariBalances: 'ACCOUNTING',
  travelCariTransactions: 'ACCOUNTING',
  travelCariMizan: 'ACCOUNTING',
};

// Module to permission mapping for Trading
const TRADING_MODULE_MAP: Record<string, PermissionModule> = {
  tradingDashboard: 'DASHBOARD',
  cariKartlar: 'CUSTOMERS',
  cariBalances: 'CUSTOMERS',
  cariTransactions: 'CUSTOMERS',
  bankMatching: 'CUSTOMERS',
  cariMizan: 'CUSTOMERS',
  cariTypes: 'ACCOUNTING',
  tradingCustomers: 'CUSTOMERS',
  tradingBrands: 'BRANDS',
  tradingModels: 'MODELS',
  tradingProducts: 'PRODUCTS',
  tradingInquiries: 'INQUIRIES',
  warehouses: 'WAREHOUSES',
  stockLevels: 'STOCK_LEVELS',
  stockMovements: 'STOCK_MOVEMENTS',
  stockLedgerReport: 'STOCK_MOVEMENTS',
  stockStatusReport: 'STOCK_MOVEMENTS',
  stockMizanReport: 'STOCK_MOVEMENTS',
  profitLossReport: 'ACCOUNTING',
  suppliers: 'SUPPLIERS',
  purchaseOrders: 'PURCHASE_ORDERS',
  goodsReceipts: 'GOODS_RECEIPTS',
  salesQuotes: 'SALES_QUOTES',
  salesOrders: 'SALES_ORDERS',
  deliveries: 'DELIVERIES',
  // Financial Documents (Phase 2)
  tradingInvoices: 'INVOICES',
  tradingPayments: 'PAYMENTS',
  supplierInvoices: 'SUPPLIER_INVOICES',
  creditNotes: 'CREDIT_NOTES',
  tradingAccounting: 'ACCOUNTING',
  tradingPostingRules: 'ACCOUNTING',
  tradingTieOut: 'ACCOUNTING',
  tradingPeriodEnd: 'ACCOUNTING',
  tradingActivityFeed: 'ACCOUNTING',
  // Consignment
  consignment: 'CONSIGNMENT',
  vendors: 'CONSIGNMENT',
};

// Travel Agency navigation
const travelNavigationGroups = [
  { key: 'travelAgency', items: [{ key: 'dashboard', href: '/travel', icon: LayoutDashboard }] },
  { key: 'outgoing', items: [
    { key: 'outgoingBookings', href: '/travel/bookings?direction=OUTGOING', icon: CalendarCheck },
    { key: 'outgoingInvoices', href: '/travel/invoices?direction=OUTGOING', icon: Receipt },
  ] },
  { key: 'incoming', items: [
    { key: 'incomingBookings', href: '/travel/bookings?direction=INCOMING', icon: CalendarCheck },
    { key: 'incomingInvoices', href: '/travel/invoices?direction=INCOMING', icon: Receipt },
  ] },
  // Unified Cari Kartlar also serves the legacy customer/partner URLs.
  { key: 'travelCariSection', items: [
    { key: 'travelCariKartlar', href: '/travel/cari', icon: Users },
    { key: 'travelCariTransactions', href: '/travel/cari/transactions', icon: ArrowLeftRight },
    { key: 'travelCariStatement', href: '/travel/cari/balances', icon: FileText },
    { key: 'travelCariMizan', href: '/travel/cari/mizan', icon: Scale },
    { key: 'travelCariTypes', href: '/travel/cari/types', icon: Settings2 },
  ] },
  { key: 'accounting', items: [
    { key: 'travelLedger', href: '/travel/accounting/reports?tab=ledger', icon: FileText },
    { key: 'travelCashBook', href: '/travel/accounting/reports?tab=cash-book', icon: Wallet },
    { key: 'travelTrialBalance', href: '/travel/accounting/reports?tab=trial-balance', icon: Scale },
    { key: 'accounting', href: '/travel/accounting', icon: Calculator },
    { key: 'expenses', href: '/travel/expenses', icon: Wallet },
    { key: 'postingRules', href: '/travel/accounting/posting-rules', icon: Settings2 },
    { key: 'tieOut', href: '/travel/accounting/tie-out', icon: Scale },
    { key: 'periodEnd', href: '/travel/accounting/period-end', icon: CalendarCheck },
  ] },
  { key: 'travelTours', items: [
    { key: 'landTours', href: '/travel/packages?tourType=LAND', icon: Compass },
    { key: 'cruise', href: '/travel/packages?tourType=CRUISE', icon: Package },
    { key: 'packages', href: '/travel/packages', icon: Package },
  ] },
  { key: 'travelManagement', items: [
    { key: 'bookings', href: '/travel/bookings', icon: CalendarCheck },
    { key: 'invoices', href: '/travel/invoices', icon: Receipt },
    { key: 'quotes', href: '/travel/quotes', icon: FileText },
    { key: 'allocations', href: '/travel/allocations', icon: Layers },
    { key: 'operations', href: '/travel/operations', icon: Calendar },
    { key: 'notifications', href: '/travel/notifications', icon: Bell },
    { key: 'promoCodes', href: '/travel/marketing/promo-codes', icon: Ticket },
    { key: 'reviews', href: '/travel/reviews', icon: MessageSquare },
  ] },
];

// Trading Business navigation - Dashboard
const tradingDashboardNavigation = [
  { key: 'tradingDashboard', href: '/trading', icon: LayoutDashboard },
];

// Trading Business navigation - Cari Kartlar (Account Cards)
const tradingCariNavigation = [
  { key: 'cariKartlar', href: '/trading/cari', icon: Users },
  { key: 'cariBalances', href: '/trading/cari/balances', icon: Wallet },
  { key: 'cariTransactions', href: '/trading/cari/transactions', icon: ArrowLeftRight },
  { key: 'bankMatching', href: '/trading/cari/bank-matching', icon: FileCheck },
  { key: 'cariMizan', href: '/trading/cari/mizan', icon: Scale },
  { key: 'cariTypes', href: '/trading/cari/types', icon: Settings2 },
];

// Trading Business navigation - Product Catalog
const tradingCatalogNavigation = [
  { key: 'tradingBrands', href: '/trading/brands', icon: Tags },
  { key: 'tradingModels', href: '/trading/models', icon: Boxes },
  { key: 'tradingProducts', href: '/trading/products', icon: Store },
  { key: 'tradingInquiries', href: '/trading/inquiries', icon: ClipboardList },
];

// Trading Business navigation - Inventory & Warehouse
const tradingInventoryNavigation = [
  { key: 'warehouses', href: '/trading/warehouses', icon: Warehouse },
  { key: 'stockLevels', href: '/trading/inventory', icon: PackageSearch },
  { key: 'stockMovements', href: '/trading/inventory/movements', icon: ArrowLeftRight },
];

// Trading Business navigation - Purchasing
const tradingPurchasingNavigation = [
  { key: 'purchaseOrders', href: '/trading/purchasing', icon: TrendingDown },
  { key: 'goodsReceipts', href: '/trading/purchasing/receipts', icon: FileCheck },
];

// Trading Business navigation - Sales
const tradingSalesNavigation = [
  { key: 'salesQuotes', href: '/trading/sales/quotes', icon: FileText },
  { key: 'salesOrders', href: '/trading/sales/orders', icon: TrendingUp },
  { key: 'deliveries', href: '/trading/sales/deliveries', icon: Truck },
];

// Trading Business navigation - Financial (Phase 2)
const tradingFinancialNavigation = [
  { key: 'tradingInvoices', href: '/trading/finance/invoices', icon: Receipt },
  { key: 'tradingPayments', href: '/trading/finance/payments', icon: CreditCard },
  { key: 'supplierInvoices', href: '/trading/finance/supplier-invoices', icon: FileInput },
  { key: 'creditNotes', href: '/trading/finance/credit-notes', icon: FileMinus },
];

// Trading Business navigation - Consignment
const tradingConsignmentNavigation = [
  { key: 'consignment', href: '/trading/consignment', icon: Handshake },
  { key: 'vendors', href: '/trading/consignment/vendors', icon: Store },
];

// Trading Business navigation - Accounting
const tradingAccountingNavigation = [
  { key: 'tradingAccounting', href: '/trading/accounting', icon: Calculator },
  { key: 'tradingPostingRules', href: '/trading/accounting/posting-rules', icon: Settings2 },
  { key: 'tradingTieOut', href: '/trading/accounting/tie-out', icon: Scale },
  { key: 'tradingPeriodEnd', href: '/trading/accounting/period-end', icon: CalendarCheck },
  { key: 'tradingActivityFeed', href: '/trading/accounting/activity', icon: History },
];

// Trading Business navigation - Reports (Raporlar)
const tradingReportsNavigation = [
  { key: 'stockLedgerReport', href: '/trading/reports/stock-ledger', icon: FileText },
  { key: 'stockStatusReport', href: '/trading/reports/stock-status', icon: PackageSearch },
  { key: 'stockMizanReport', href: '/trading/reports/stock-mizan', icon: Calculator },
  { key: 'profitLossReport', href: '/trading/reports/profit-loss', icon: TrendingUp },
];

const bottomNavigation = [
  { key: 'settings', href: '/settings', icon: Settings },
];

export function Sidebar({ isOpen, onToggle, isMobile = false }: SidebarProps) {
  const location = useLocation();
  const { t } = useTranslation('sidebar');
  const { currentApp, hasPermissionForApp, canAccessBothApps } = useAppStore();

  // Helper to check if a nav item should be shown based on permissions
  // If permissions aren't loaded yet, show everything (will be filtered after login)
  const canShowTravelItem = (key: string) => {
    const { permissions } = useAppStore.getState();
    // Show all if no permissions loaded yet (legacy session) or super admin
    if (!permissions || permissions.isSuperAdmin) return true;
    if (key === 'travelCariKartlar') return ['CUSTOMERS', 'PARTNERS', 'ACCOUNTING'].some(module =>
      hasPermissionForApp('TRAVEL', module as PermissionModule, 'VIEW')
    );
    const module = TRAVEL_MODULE_MAP[key];
    if (!module) return true; // Settings etc.
    return hasPermissionForApp('TRAVEL', module, 'VIEW');
  };

  const canShowTradingItem = (key: string) => {
    const { permissions } = useAppStore.getState();
    // Show all if no permissions loaded yet (legacy session) or super admin
    if (!permissions || permissions.isSuperAdmin) return true;
    const module = TRADING_MODULE_MAP[key];
    if (!module) return true;
    return hasPermissionForApp('TRADING', module, 'VIEW');
  };

  // Filter navigation based on permissions
  const filteredTravelGroups = travelNavigationGroups.map(group => ({ ...group, items: group.items.filter(item => canShowTravelItem(item.key)) })).filter(group => group.items.length > 0);
  const filteredTradingDashboard = tradingDashboardNavigation.filter((item) => canShowTradingItem(item.key));
  const filteredTradingCari = tradingCariNavigation.filter((item) => canShowTradingItem(item.key));
  const filteredTradingReports = tradingReportsNavigation.filter((item) => canShowTradingItem(item.key));
  const filteredTradingCatalog = tradingCatalogNavigation.filter((item) => canShowTradingItem(item.key));
  const filteredTradingInventory = tradingInventoryNavigation.filter((item) => canShowTradingItem(item.key));
  const filteredTradingPurchasing = tradingPurchasingNavigation.filter((item) => canShowTradingItem(item.key));
  const filteredTradingSales = tradingSalesNavigation.filter((item) => canShowTradingItem(item.key));
  const filteredTradingFinancial = tradingFinancialNavigation.filter((item) => canShowTradingItem(item.key));
  const filteredTradingConsignment = tradingConsignmentNavigation.filter((item) => canShowTradingItem(item.key));
  const filteredTradingAccounting = tradingAccountingNavigation.filter((item) => canShowTradingItem(item.key));

  // Default to TRAVEL if no app is selected yet (handles initial load)
  const effectiveApp = currentApp || 'TRAVEL';
  const showTravel = effectiveApp === 'TRAVEL';
  const showTrading = effectiveApp === 'TRADING';

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-[hsl(var(--sidebar-border))]',
        'bg-[hsl(var(--sidebar-background))] text-[hsl(var(--sidebar-foreground))]',
        'shrink-0',
        isMobile
          ? cn(
              'fixed inset-y-0 left-0 z-50 w-72 shadow-2xl',
              'transition-transform duration-300 ease-out',
              isOpen ? 'translate-x-0' : '-translate-x-full'
            )
          : cn(
              'relative transition-[width] duration-300 ease-out',
              isOpen ? 'w-72' : 'w-20'
            )
      )}
    >

      {/* Logo Section */}
      <div className="relative flex h-20 items-center border-b border-[hsl(var(--sidebar-border))] px-5">
        <div className="flex items-center gap-3 overflow-hidden">
          {/* Logo Icon */}
          {effectiveApp === 'TRADING' ? (
            <div className="relative flex-shrink-0 h-10 w-10 bg-white">
              <img
                src="/polatkan-trading-mark.png"
                alt="Polatkan Trading"
                className="h-full w-full object-contain"
              />
            </div>
          ) : (
            <div className="relative flex-shrink-0 flex h-10 w-10 items-center justify-center bg-[hsl(var(--sidebar-primary))]">
              <Compass className="h-5 w-5 text-[hsl(var(--sidebar-background))]" />
            </div>
          )}

          {/* Brand Text */}
          <div
            className={cn(
              'overflow-hidden whitespace-nowrap transition-all duration-300',
              isOpen ? 'w-auto opacity-100' : 'w-0 opacity-0'
            )}
          >
            <h1 className="font-display text-xl font-semibold tracking-tight text-white">
              Polatkan
            </h1>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--sidebar-muted))]">
              {effectiveApp === 'TRADING' ? 'Trading' : 'Travel Agency'}
            </p>
          </div>
        </div>
      </div>

      {/* App Selector - only show when sidebar is open and user has both apps */}
      {canAccessBothApps() && isOpen && (
        <div className="px-4 py-3 border-b border-[hsl(var(--sidebar-border))]">
          <AppSelectorCompact />
        </div>
      )}

      {/* Main Navigation */}
      <nav className="flex-1 overflow-y-auto py-6 px-3 hide-scrollbar">
        {showTravel && filteredTravelGroups.map(group => (
          <div key={group.key} className="mb-5 space-y-1">
            {isOpen && <p className="mb-2 px-3 text-[10px] font-medium uppercase tracking-[0.15em] text-[hsl(var(--sidebar-muted))]">{t(group.key)}</p>}
            {group.items.map(item => {
              const active = isTravelNavigationActive(location.pathname, location.search, item.href);
              return <Link key={item.key} to={item.href} title={t(item.key)} aria-current={active ? 'page' : undefined}
                onClick={() => { if (isMobile) onToggle(); }}
                className={cn('group relative flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors',
                  active ? 'bg-[hsl(var(--sidebar-accent))] text-white' : 'text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))/50] hover:text-white')}>
                <item.icon className={cn('h-5 w-5 shrink-0', active ? 'text-[hsl(var(--sidebar-primary))]' : 'text-[hsl(var(--sidebar-muted))]')} />
                {isOpen && <span className="whitespace-nowrap">{t(item.key)}</span>}
              </Link>;
            })}
          </div>
        ))}

        {/* Trading Section - Dashboard */}
        {showTrading && filteredTradingDashboard.length > 0 && (
          <>
            <div className="space-y-1 mb-4">
              {filteredTradingDashboard.map((item) => {
                const isActive = location.pathname === item.href;

                return (
                  <NavLink
                    key={item.key}
                    to={item.href}
                    className={cn(
                      'group relative flex items-center gap-3 px-3 py-2.5 text-sm font-medium',
                      'transition-colors duration-200',
                      isActive
                        ? 'bg-[hsl(var(--sidebar-accent))] text-white'
                        : 'text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))/50] hover:text-white'
                    )}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 bg-gradient-to-b from-amber-500 to-amber-600" />
                    )}

                    <item.icon
                      className={cn(
                        'h-5 w-5 flex-shrink-0',
                        isActive
                          ? 'text-amber-500'
                          : 'text-[hsl(var(--sidebar-muted))] group-hover:text-white'
                      )}
                    />

                    <span
                      className={cn(
                        'overflow-hidden whitespace-nowrap transition-all duration-300',
                        isOpen ? 'w-auto opacity-100' : 'w-0 opacity-0'
                      )}
                    >
                      {t(item.key)}
                    </span>

                    {!isOpen && (
                      <div className="absolute left-full ml-3 hidden bg-amber-600 px-3 py-1.5 text-xs font-medium text-white group-hover:block z-50">
                        {t(item.key)}
                        <div className="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 bg-amber-600" />
                      </div>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </>
        )}

        {/* Trading Section - Cari Kartlar */}
        {showTrading && filteredTradingCari.length > 0 && (
          <>
            <div className="space-y-1 mb-4">
              {filteredTradingCari.map((item) => {
                const GLOBAL_CARI_PATHS = ['/trading/cari/balances', '/trading/cari/transactions', '/trading/cari/bank-matching', '/trading/cari/mizan', '/trading/cari/types'];
                const isActive =
                  item.href === '/trading/cari'
                    ? location.pathname === '/trading/cari' ||
                      location.pathname.startsWith('/trading/customers') ||
                      (location.pathname.startsWith('/trading/cari/') &&
                        !GLOBAL_CARI_PATHS.some((p) => location.pathname.startsWith(p)))
                    : location.pathname.startsWith(item.href);

                return (
                  <NavLink
                    key={item.key}
                    to={item.href}
                    className={cn(
                      'group relative flex items-center gap-3 px-3 py-2.5 text-sm font-medium',
                      'transition-colors duration-200',
                      isActive
                        ? 'bg-[hsl(var(--sidebar-accent))] text-white'
                        : 'text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))/50] hover:text-white'
                    )}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 bg-gradient-to-b from-blue-500 to-blue-600" />
                    )}

                    <item.icon
                      className={cn(
                        'h-5 w-5 flex-shrink-0',
                        isActive
                          ? 'text-blue-400'
                          : 'text-[hsl(var(--sidebar-muted))] group-hover:text-white'
                      )}
                    />

                    <span
                      className={cn(
                        'overflow-hidden whitespace-nowrap transition-all duration-300',
                        isOpen ? 'w-auto opacity-100' : 'w-0 opacity-0'
                      )}
                    >
                      {t(item.key)}
                    </span>

                    {!isOpen && (
                      <div className="absolute left-full ml-3 hidden bg-blue-600 px-3 py-1.5 text-xs font-medium text-white group-hover:block z-50">
                        {t(item.key)}
                        <div className="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 bg-blue-600" />
                      </div>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </>
        )}

        {/* Trading Section - Catalog */}
        {showTrading && filteredTradingCatalog.length > 0 && (
          <>
            <div
              className={cn(
                'mb-4 px-3 overflow-hidden whitespace-nowrap transition-all duration-300',
                isOpen ? 'opacity-100' : 'opacity-0'
              )}
            >
              <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-[hsl(var(--sidebar-muted))]">
                {t('tradingCatalog')}
              </p>
            </div>

            <div className="space-y-1">
              {filteredTradingCatalog.map((item) => {
                const isActive = location.pathname.startsWith(item.href);

                return (
                  <NavLink
                    key={item.key}
                    to={item.href}
                    className={cn(
                      'group relative flex items-center gap-3 px-3 py-2.5 text-sm font-medium',
                      'transition-colors duration-200',
                      isActive
                        ? 'bg-[hsl(var(--sidebar-accent))] text-white'
                        : 'text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))/50] hover:text-white'
                    )}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 bg-gradient-to-b from-emerald-500 to-emerald-600" />
                    )}

                    <item.icon
                      className={cn(
                        'h-5 w-5 flex-shrink-0',
                        isActive
                          ? 'text-emerald-400'
                          : 'text-[hsl(var(--sidebar-muted))] group-hover:text-white'
                      )}
                    />

                    <span
                      className={cn(
                        'overflow-hidden whitespace-nowrap transition-all duration-300',
                        isOpen ? 'w-auto opacity-100' : 'w-0 opacity-0'
                      )}
                    >
                      {t(item.key)}
                    </span>

                    {!isOpen && (
                      <div className="absolute left-full ml-3 hidden bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white group-hover:block z-50">
                        {t(item.key)}
                        <div className="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 bg-emerald-600" />
                      </div>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </>
        )}

        {/* Trading Section - Inventory */}
        {showTrading && filteredTradingInventory.length > 0 && (
          <>
            <div
              className={cn(
                'mt-6 mb-4 px-3 overflow-hidden whitespace-nowrap transition-all duration-300',
                isOpen ? 'opacity-100' : 'opacity-0'
              )}
            >
              <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-[hsl(var(--sidebar-muted))]">
                {t('tradingInventory')}
              </p>
            </div>

            <div className="space-y-1">
              {filteredTradingInventory.map((item) => {
                const isActive = location.pathname === item.href ||
                  (item.href !== '/trading/inventory' && location.pathname.startsWith(item.href));

                return (
                  <NavLink
                    key={item.key}
                    to={item.href}
                    className={cn(
                      'group relative flex items-center gap-3 px-3 py-2.5 text-sm font-medium',
                      'transition-colors duration-200',
                      isActive
                        ? 'bg-[hsl(var(--sidebar-accent))] text-white'
                        : 'text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))/50] hover:text-white'
                    )}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 bg-gradient-to-b from-cyan-500 to-cyan-600" />
                    )}

                    <item.icon
                      className={cn(
                        'h-5 w-5 flex-shrink-0',
                        isActive
                          ? 'text-cyan-400'
                          : 'text-[hsl(var(--sidebar-muted))] group-hover:text-white'
                      )}
                    />

                    <span
                      className={cn(
                        'overflow-hidden whitespace-nowrap transition-all duration-300',
                        isOpen ? 'w-auto opacity-100' : 'w-0 opacity-0'
                      )}
                    >
                      {t(item.key)}
                    </span>

                    {!isOpen && (
                      <div className="absolute left-full ml-3 hidden bg-cyan-600 px-3 py-1.5 text-xs font-medium text-white group-hover:block z-50">
                        {t(item.key)}
                        <div className="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 bg-cyan-600" />
                      </div>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </>
        )}

        {/* Trading Section - Purchasing */}
        {showTrading && filteredTradingPurchasing.length > 0 && (
          <>
            <div
              className={cn(
                'mt-6 mb-4 px-3 overflow-hidden whitespace-nowrap transition-all duration-300',
                isOpen ? 'opacity-100' : 'opacity-0'
              )}
            >
              <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-[hsl(var(--sidebar-muted))]">
                {t('tradingPurchasing')}
              </p>
            </div>

            <div className="space-y-1">
              {filteredTradingPurchasing.map((item) => {
                const isActive = location.pathname === item.href ||
                  (item.href !== '/trading/purchasing' && location.pathname.startsWith(item.href));

                return (
                  <NavLink
                    key={item.key}
                    to={item.href}
                    className={cn(
                      'group relative flex items-center gap-3 px-3 py-2.5 text-sm font-medium',
                      'transition-colors duration-200',
                      isActive
                        ? 'bg-[hsl(var(--sidebar-accent))] text-white'
                        : 'text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))/50] hover:text-white'
                    )}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 bg-gradient-to-b from-orange-500 to-orange-600" />
                    )}

                    <item.icon
                      className={cn(
                        'h-5 w-5 flex-shrink-0',
                        isActive
                          ? 'text-orange-400'
                          : 'text-[hsl(var(--sidebar-muted))] group-hover:text-white'
                      )}
                    />

                    <span
                      className={cn(
                        'overflow-hidden whitespace-nowrap transition-all duration-300',
                        isOpen ? 'w-auto opacity-100' : 'w-0 opacity-0'
                      )}
                    >
                      {t(item.key)}
                    </span>

                    {!isOpen && (
                      <div className="absolute left-full ml-3 hidden bg-orange-600 px-3 py-1.5 text-xs font-medium text-white group-hover:block z-50">
                        {t(item.key)}
                        <div className="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 bg-orange-600" />
                      </div>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </>
        )}

        {/* Trading Section - Sales */}
        {showTrading && filteredTradingSales.length > 0 && (
          <>
            <div
              className={cn(
                'mt-6 mb-4 px-3 overflow-hidden whitespace-nowrap transition-all duration-300',
                isOpen ? 'opacity-100' : 'opacity-0'
              )}
            >
              <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-[hsl(var(--sidebar-muted))]">
                {t('tradingSales')}
              </p>
            </div>

            <div className="space-y-1">
              {filteredTradingSales.map((item) => {
                const isActive = location.pathname.startsWith(item.href);

                return (
                  <NavLink
                    key={item.key}
                    to={item.href}
                    className={cn(
                      'group relative flex items-center gap-3 px-3 py-2.5 text-sm font-medium',
                      'transition-colors duration-200',
                      isActive
                        ? 'bg-[hsl(var(--sidebar-accent))] text-white'
                        : 'text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))/50] hover:text-white'
                    )}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 bg-gradient-to-b from-violet-500 to-violet-600" />
                    )}

                    <item.icon
                      className={cn(
                        'h-5 w-5 flex-shrink-0',
                        isActive
                          ? 'text-violet-400'
                          : 'text-[hsl(var(--sidebar-muted))] group-hover:text-white'
                      )}
                    />

                    <span
                      className={cn(
                        'overflow-hidden whitespace-nowrap transition-all duration-300',
                        isOpen ? 'w-auto opacity-100' : 'w-0 opacity-0'
                      )}
                    >
                      {t(item.key)}
                    </span>

                    {!isOpen && (
                      <div className="absolute left-full ml-3 hidden bg-violet-600 px-3 py-1.5 text-xs font-medium text-white group-hover:block z-50">
                        {t(item.key)}
                        <div className="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 bg-violet-600" />
                      </div>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </>
        )}

        {/* Trading Section - Financial */}
        {showTrading && filteredTradingFinancial.length > 0 && (
          <>
            <div
              className={cn(
                'mt-6 mb-4 px-3 overflow-hidden whitespace-nowrap transition-all duration-300',
                isOpen ? 'opacity-100' : 'opacity-0'
              )}
            >
              <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-[hsl(var(--sidebar-muted))]">
                {t('tradingFinancial')}
              </p>
            </div>

            <div className="space-y-1">
              {filteredTradingFinancial.map((item) => {
                const isActive = location.pathname.startsWith(item.href);

                return (
                  <NavLink
                    key={item.key}
                    to={item.href}
                    className={cn(
                      'group relative flex items-center gap-3 px-3 py-2.5 text-sm font-medium',
                      'transition-colors duration-200',
                      isActive
                        ? 'bg-[hsl(var(--sidebar-accent))] text-white'
                        : 'text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))/50] hover:text-white'
                    )}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 bg-gradient-to-b from-rose-500 to-rose-600" />
                    )}

                    <item.icon
                      className={cn(
                        'h-5 w-5 flex-shrink-0',
                        isActive
                          ? 'text-rose-400'
                          : 'text-[hsl(var(--sidebar-muted))] group-hover:text-white'
                      )}
                    />

                    <span
                      className={cn(
                        'overflow-hidden whitespace-nowrap transition-all duration-300',
                        isOpen ? 'w-auto opacity-100' : 'w-0 opacity-0'
                      )}
                    >
                      {t(item.key)}
                    </span>

                    {!isOpen && (
                      <div className="absolute left-full ml-3 hidden bg-rose-600 px-3 py-1.5 text-xs font-medium text-white group-hover:block z-50">
                        {t(item.key)}
                        <div className="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 bg-rose-600" />
                      </div>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </>
        )}

        {/* Trading Section - Consignment */}
        {showTrading && filteredTradingConsignment.length > 0 && (
          <>
            <div
              className={cn(
                'mt-6 mb-4 px-3 overflow-hidden whitespace-nowrap transition-all duration-300',
                isOpen ? 'opacity-100' : 'opacity-0'
              )}
            >
              <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-[hsl(var(--sidebar-muted))]">
                {t('consignment')}
              </p>
            </div>

            <div className="space-y-1">
              {filteredTradingConsignment.map((item) => {
                const isActive = location.pathname.startsWith(item.href);

                return (
                  <NavLink
                    key={item.key}
                    to={item.href}
                    className={cn(
                      'group relative flex items-center gap-3 px-3 py-2.5 text-sm font-medium',
                      'transition-colors duration-200',
                      isActive
                        ? 'bg-[hsl(var(--sidebar-accent))] text-white'
                        : 'text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))/50] hover:text-white'
                    )}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 bg-gradient-to-b from-fuchsia-500 to-fuchsia-600" />
                    )}

                    <item.icon
                      className={cn(
                        'h-5 w-5 flex-shrink-0',
                        isActive
                          ? 'text-fuchsia-400'
                          : 'text-[hsl(var(--sidebar-muted))] group-hover:text-white'
                      )}
                    />

                    <span
                      className={cn(
                        'overflow-hidden whitespace-nowrap transition-all duration-300',
                        isOpen ? 'w-auto opacity-100' : 'w-0 opacity-0'
                      )}
                    >
                      {t(item.key)}
                    </span>

                    {!isOpen && (
                      <div className="absolute left-full ml-3 hidden bg-fuchsia-600 px-3 py-1.5 text-xs font-medium text-white group-hover:block z-50">
                        {t(item.key)}
                        <div className="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 bg-fuchsia-600" />
                      </div>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </>
        )}

        {/* Trading Section - Accounting */}
        {showTrading && filteredTradingAccounting.length > 0 && (
          <>
            <div
              className={cn(
                'mt-6 mb-4 px-3 overflow-hidden whitespace-nowrap transition-all duration-300',
                isOpen ? 'opacity-100' : 'opacity-0'
              )}
            >
              <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-[hsl(var(--sidebar-muted))]">
                {t('tradingAccountingSection')}
              </p>
            </div>

            <div className="space-y-1">
              {filteredTradingAccounting.map((item) => {
                // The posting-rules page has its own entry; keep the hub entry quiet there.
                const isActive =
                  item.href === '/trading/accounting'
                    ? location.pathname.startsWith(item.href) &&
                      !location.pathname.startsWith('/trading/accounting/posting-rules') &&
                      !location.pathname.startsWith('/trading/accounting/tie-out') &&
                      !location.pathname.startsWith('/trading/accounting/period-end') &&
                      !location.pathname.startsWith('/trading/accounting/activity')
                    : location.pathname.startsWith(item.href);

                return (
                  <NavLink
                    key={item.key}
                    to={item.href}
                    className={cn(
                      'group relative flex items-center gap-3 px-3 py-2.5 text-sm font-medium',
                      'transition-colors duration-200',
                      isActive
                        ? 'bg-[hsl(var(--sidebar-accent))] text-white'
                        : 'text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))/50] hover:text-white'
                    )}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 bg-gradient-to-b from-indigo-500 to-indigo-600" />
                    )}

                    <item.icon
                      className={cn(
                        'h-5 w-5 flex-shrink-0',
                        isActive
                          ? 'text-indigo-400'
                          : 'text-[hsl(var(--sidebar-muted))] group-hover:text-white'
                      )}
                    />

                    <span
                      className={cn(
                        'overflow-hidden whitespace-nowrap transition-all duration-300',
                        isOpen ? 'w-auto opacity-100' : 'w-0 opacity-0'
                      )}
                    >
                      {t(item.key)}
                    </span>

                    {!isOpen && (
                      <div className="absolute left-full ml-3 hidden bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white group-hover:block z-50">
                        {t(item.key)}
                        <div className="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 bg-indigo-600" />
                      </div>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </>
        )}

        {/* Trading Section - Reports (Raporlar) */}
        {showTrading && filteredTradingReports.length > 0 && (
          <>
            <div
              className={cn(
                'mt-6 mb-4 px-3 overflow-hidden whitespace-nowrap transition-all duration-300',
                isOpen ? 'opacity-100' : 'opacity-0'
              )}
            >
              <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-[hsl(var(--sidebar-muted))]">
                {t('tradingReportsSection')}
              </p>
            </div>

            <div className="space-y-1">
              {filteredTradingReports.map((item) => {
                const isActive = location.pathname.startsWith(item.href);

                return (
                  <NavLink
                    key={item.key}
                    to={item.href}
                    className={cn(
                      'group relative flex items-center gap-3 px-3 py-2.5 text-sm font-medium',
                      'transition-colors duration-200',
                      isActive
                        ? 'bg-[hsl(var(--sidebar-accent))] text-white'
                        : 'text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))/50] hover:text-white'
                    )}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 bg-gradient-to-b from-emerald-500 to-emerald-600" />
                    )}

                    <item.icon
                      className={cn(
                        'h-5 w-5 flex-shrink-0',
                        isActive ? 'text-emerald-400' : 'text-[hsl(var(--sidebar-muted))] group-hover:text-white'
                      )}
                    />

                    <span
                      className={cn(
                        'overflow-hidden whitespace-nowrap transition-all duration-300',
                        isOpen ? 'w-auto opacity-100' : 'w-0 opacity-0'
                      )}
                    >
                      {t(item.key)}
                    </span>

                    {!isOpen && (
                      <div className="absolute left-full ml-3 hidden bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white group-hover:block z-50">
                        {t(item.key)}
                        <div className="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 bg-emerald-600" />
                      </div>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </>
        )}
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-[hsl(var(--sidebar-border))] py-4 px-3">
        {bottomNavigation.map((item) => {
          const isActive = location.pathname.startsWith(item.href);

          return (
            <NavLink
              key={item.key}
              to={item.href}
              className={cn(
                'group relative flex items-center gap-3 px-3 py-2.5 text-sm font-medium',
                'transition-colors duration-200',
                isActive
                  ? 'bg-[hsl(var(--sidebar-accent))] text-white'
                  : 'text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-accent))/50] hover:text-white'
              )}
            >
              {isActive && (
                <div className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 bg-gradient-to-b from-[hsl(var(--sidebar-primary))] to-[hsl(38,70%,45%)]" />
              )}

              <item.icon
                className={cn(
                  'h-5 w-5 flex-shrink-0',
                  isActive
                    ? 'text-[hsl(var(--sidebar-primary))]'
                    : 'text-[hsl(var(--sidebar-muted))] group-hover:text-white'
                )}
              />

              <span
                className={cn(
                  'overflow-hidden whitespace-nowrap transition-all duration-300',
                  isOpen ? 'w-auto opacity-100' : 'w-0 opacity-0'
                )}
              >
                {t(item.key)}
              </span>

              {!isOpen && (
                <div className="absolute left-full ml-3 hidden bg-[hsl(var(--primary))] px-3 py-1.5 text-xs font-medium text-white group-hover:block z-50">
                  {t(item.key)}
                  <div className="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 bg-[hsl(var(--primary))]" />
                </div>
              )}
            </NavLink>
          );
        })}

        {/* Version Footer */}
        <div
          className={cn(
            'mt-4 px-3 overflow-hidden whitespace-nowrap transition-all duration-300',
            isOpen ? 'opacity-100' : 'opacity-0 h-0'
          )}
        >
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 bg-emerald-400 animate-pulse flex-shrink-0" />
            <span className="text-[10px] text-[hsl(var(--sidebar-muted))]">
              v0.1.0 • All systems operational
            </span>
          </div>
        </div>
      </div>

      {/* Toggle Button — hidden on mobile (hamburger menu handles it) */}
      {!isMobile && (
        <button
          onClick={onToggle}
          className={cn(
            'absolute -right-3 top-24 flex h-6 w-6 items-center justify-center',
            'border border-border bg-background',
            'transition-colors duration-200',
            'hover:bg-muted',
            'focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20',
            'text-muted-foreground'
          )}
          title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {isOpen ? (
            <ChevronLeft className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </button>
      )}
    </aside>
  );
}
